import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DesktopSellerPageContainer } from '@/components/desktop/DesktopSellerPageContainer';
import { sellerService } from '@/services/seller';
import { useToast } from '@/components/common/Toast';
import type { OrderWithItems, SalesMetrics } from '@/store/seller';
import { getEffectiveOrderTime } from '@/utils/orderTime';

export function DesktopSalesHistoryPage() {
  const navigate = useNavigate();
  const { ToastContainer, showToast } = useToast();

  const [salesMetrics, setSalesMetrics] = useState<SalesMetrics | null>(null);
  const [completedOrders, setCompletedOrders] = useState<OrderWithItems[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 월 선택 옵션 생성 (최근 6개월)
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const value = `${year}-${month.toString().padStart(2, '0')}`;
      const label = `${year}년 ${month}월`;
      
      options.push({ value, label });
    }
    
    return options;
  };

  const monthOptions = generateMonthOptions();

  useEffect(() => {
    // 기본값을 현재 월로 설정
    if (monthOptions.length > 0) {
      setSelectedMonth(monthOptions[0].value);
    }
  }, []);

  useEffect(() => {
    const loadSalesData = async () => {
      if (!selectedMonth) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // 판매자 레스토랑 ID 가져오기
        const restaurantResult = await sellerService.getSellerRestaurantId();
        if (!restaurantResult.success || !restaurantResult.restaurantId) {
          setError(restaurantResult.error || '레스토랑 정보를 찾을 수 없습니다');
          return;
        }

        // 대시보드 데이터 가져오기 (매출 정보)
        const dashboardResult = await sellerService.getDashboardData(restaurantResult.restaurantId);
        if (dashboardResult.success && dashboardResult.data) {
          setSalesMetrics(dashboardResult.data.salesMetrics);
        }

        // 완료된 주문 목록 가져오기
        const ordersResult = await sellerService.getOrdersByStatus(restaurantResult.restaurantId, 'COMPLETED');
        if (ordersResult.success && ordersResult.data) {
          // 선택된 월에 해당하는 주문만 필터링
          const filteredOrders = ordersResult.data.filter(order => {
            const orderDate = new Date(order.created_at);
            const orderMonth = `${orderDate.getFullYear()}-${(orderDate.getMonth() + 1).toString().padStart(2, '0')}`;
            return orderMonth === selectedMonth;
          });
          
          setCompletedOrders(filteredOrders);
        }
        
      } catch (error) {
        console.error('Sales data load error:', error);
        setError('판매 데이터를 불러오는 중 오류가 발생했습니다');
      } finally {
        setLoading(false);
      }
    };

    loadSalesData();
  }, [selectedMonth]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ko-KR').format(amount);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getOrderTypeLabel = (type: string) => {
    const labels = {
      'DEADLINE_DISCOUNT': '마감할인',
      'RESERVATION_DISCOUNT': '예약할인',
      'REGULAR_ORDER': '일반주문'
    };
    return labels[type as keyof typeof labels] || type;
  };

  const getOrderTypeBadgeColor = (type: string) => {
    const colors = {
      'DEADLINE_DISCOUNT': 'bg-orange-100 text-orange-600',
      'RESERVATION_DISCOUNT': 'bg-blue-100 text-blue-600',
      'REGULAR_ORDER': 'bg-gray-100 text-gray-600'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-600';
  };

  const getItemsText = (items: any[]) => {
    if (!items || items.length === 0) {
      return '상품 정보 없음';
    }
    
    const firstItem = items[0];
    const restCount = items.length - 1;
    
    if (restCount > 0) {
      return `${firstItem.product_name} 외 ${restCount}개`;
    }
    
    return firstItem.product_name;
  };

  const handleReviewClick = () => {
    showToast('아직 비활성화된 기능입니다');
  };

  if (loading) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-6"></div>
            <p className="text-[18px] text-gray-600">판매 데이터를 불러오는 중...</p>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  if (error) {
    return (
      <DesktopSellerPageContainer>
        <div className="flex items-center justify-center min-h-[600px]">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-6">⚠️</div>
            <p className="text-[18px] text-gray-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/seller/store')}
              className="px-6 py-3 bg-green-500 text-white text-[16px] font-semibold rounded-lg hover:bg-green-600 transition-colors"
            >
              내 가게로 돌아가기
            </button>
          </div>
        </div>
      </DesktopSellerPageContainer>
    );
  }

  return (
    <DesktopSellerPageContainer>
      <div className="max-w-6xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center mb-4">
            <button
              onClick={() => navigate('/seller/store')}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-[32px] font-bold text-gray-800">판매내역</h1>
          </div>

          {/* 매출 기간 선택 */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[24px] font-semibold text-gray-800">매출 기간</h2>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg text-[16px] focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {monthOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 매출 현황 - 그리드 레이아웃 */}
            {salesMetrics && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-xl p-6">
                  <h3 className="text-[18px] text-green-600 font-semibold mb-2">월 매출</h3>
                  <p className="text-[32px] font-bold text-green-700">
                    {formatCurrency(salesMetrics.monthlyRevenue)}원
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-[18px] text-blue-600 font-semibold mb-2">판매 건수</h3>
                  <p className="text-[32px] font-bold text-blue-700">
                    {salesMetrics.monthlySalesCount}건
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 판매 내역 테이블 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-[24px] font-semibold text-gray-800">주문 목록</h3>
          </div>

          {completedOrders.length === 0 ? (
            <div className="p-12 text-center">
              <p className="text-[18px] text-gray-500">선택한 월에 판매 내역이 없습니다.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">주문 유형</th>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">주문번호</th>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">주문자</th>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">주문품목</th>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">주문일시</th>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">금액</th>
                    <th className="px-6 py-4 text-left text-[14px] font-semibold text-gray-700 uppercase tracking-wider">액션</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {completedOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 rounded-full text-[12px] font-semibold ${getOrderTypeBadgeColor(order.order_type)}`}>
                          {getOrderTypeLabel(order.order_type)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[14px] font-medium text-gray-900">
                        {order.order_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[14px] text-gray-900">
                        {order.buyer_nickname}
                      </td>
                      <td className="px-6 py-4 text-[14px] text-gray-900 max-w-xs truncate">
                        {getItemsText(order.items)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[14px] text-gray-900">
                        {formatDateTime(getEffectiveOrderTime(order) || order.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-[14px] font-semibold text-green-600">
                        {formatCurrency(order.total_amount)}원
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => navigate(`/seller/orders/${order.id}`, {
                              state: { returnTo: '/seller/store/sales-history' }
                            })}
                            className="px-3 py-1 bg-gray-100 text-gray-700 text-[12px] font-medium rounded hover:bg-gray-200 transition-colors"
                          >
                            상세보기
                          </button>
                          <button
                            onClick={handleReviewClick}
                            className="px-3 py-1 bg-blue-100 text-blue-700 text-[12px] font-medium rounded hover:bg-blue-200 transition-colors"
                          >
                            리뷰보기
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </DesktopSellerPageContainer>
  );
}
